// Step 8 of the client-side X12 pipeline: the pure state machine behind the
// editor. Owns the load → parse → validate lifecycle and the one mutation
// the tool supports — changing a single element's value — re-validating
// after every change so the advisory findings shown next to a field are
// always current. hooks/useEdiDocument.ts is a thin `useReducer` wrapper
// around this for components to consume.

import {tokenize, type EdiDelimiters} from './tokenizer';
import {buildEnvelope} from './envelope';
import {
  attachExplainedTransactions,
  type EdiEnvelopeExplained,
} from './explainedEnvelope';
import {parse850Segments} from './parser850';
import {parse852Segments} from './parser852';
import {validate, type ValidationFinding} from './validator';

/** Coordinates for exactly one element to change: which segment (by its
 * index within its transaction's flat body list — the same list
 * lib/serializer.ts reads from) and which 1-indexed element position on it. */
export interface ElementPath {
  groupIndex: number;
  transactionIndex: number;
  segmentIndex: number;
  elementPosition: number;
}

export type EdiDocumentStatus = 'empty' | 'loaded' | 'error';

export interface EdiDocumentState {
  status: EdiDocumentStatus;
  envelope?: EdiEnvelopeExplained;
  delimiters?: EdiDelimiters;
  findings?: ValidationFinding[];
  activeGroupIndex: number;
  activeTransactionIndex: number;
  loadError?: string;
}

export const initialEdiDocumentState: EdiDocumentState = {
  status: 'empty',
  activeGroupIndex: 0,
  activeTransactionIndex: 0,
};

export type EdiDocumentAction =
  | {type: 'LOAD_RAW'; text: string}
  | {type: 'EDIT_ELEMENT'; path: ElementPath; value: string}
  | {type: 'SELECT_TRANSACTION'; groupIndex: number; transactionIndex: number}
  | {type: 'RESET'};

/** Immutably replaces one element's value: a new elements array, a new
 * segment object, a new segments array, a new transaction (with its
 * parsed850/parsed852 recomputed so every loop view — header, Name loops,
 * item loops — reflects the change too), a new group, a new envelope. */
function replaceElementValue(
  envelope: EdiEnvelopeExplained,
  path: ElementPath,
  value: string
): EdiEnvelopeExplained {
  return {
    ...envelope,
    groups: envelope.groups.map((group, gi) => {
      if (gi !== path.groupIndex) return group;
      return {
        ...group,
        transactions: group.transactions.map((transaction, ti) => {
          if (ti !== path.transactionIndex) return transaction;

          const segments = transaction.segments.map((segment, si) => {
            if (si !== path.segmentIndex) return segment;
            const elements = [...segment.elements];
            elements[path.elementPosition - 1] = value;
            return {...segment, elements};
          });

          return {
            ...transaction,
            segments,
            parsed850:
              transaction.transactionSetCode === '850'
                ? parse850Segments(segments)
                : undefined,
            parsed852:
              transaction.transactionSetCode === '852'
                ? parse852Segments(segments)
                : undefined,
          };
        }),
      };
    }),
  };
}

/**
 * The document editor's reducer. `LOAD_RAW` runs the full tokenize →
 * structure → parse → validate pipeline; a document that fails to tokenize
 * lands on `status: 'error'` with a message rather than throwing.
 * `EDIT_ELEMENT` changes exactly the one value named by `path` and
 * re-validates. `SELECT_TRANSACTION` only ever changes which transaction is
 * active. `RESET` returns to the empty state.
 */
export function ediDocumentReducer(
  state: EdiDocumentState,
  action: EdiDocumentAction
): EdiDocumentState {
  switch (action.type) {
    case 'LOAD_RAW': {
      const tokenized = tokenize(action.text);
      if (!tokenized.ok) {
        return {
          ...initialEdiDocumentState,
          status: 'error',
          loadError: tokenized.error,
        };
      }
      const envelope = attachExplainedTransactions(
        buildEnvelope(tokenized.segments)
      );
      return {
        status: 'loaded',
        envelope,
        delimiters: tokenized.delimiters,
        findings: validate(envelope),
        activeGroupIndex: 0,
        activeTransactionIndex: 0,
        loadError: undefined,
      };
    }

    case 'EDIT_ELEMENT': {
      if (state.status !== 'loaded' || !state.envelope) return state;
      const envelope = replaceElementValue(
        state.envelope,
        action.path,
        action.value
      );
      return {...state, envelope, findings: validate(envelope)};
    }

    case 'SELECT_TRANSACTION': {
      if (state.status !== 'loaded') return state;
      return {
        ...state,
        activeGroupIndex: action.groupIndex,
        activeTransactionIndex: action.transactionIndex,
      };
    }

    case 'RESET':
      return initialEdiDocumentState;

    default:
      return state;
  }
}
